import type { ReactElement } from "react";
import { formatUserDisplay } from "./display";
import { useUserDirectoryEntry } from "./hooks";

export interface UserNameProps {
    /**
     * The HR user id to resolve. Empty / nullish renders an em-dash so
     * detail panels can render the component unconditionally even when
     * the underlying field is optional.
     */
    readonly id: string | null | undefined;
}

/**
 * Display-only component that renders an HR user reference as
 * "Display Name (USR471)", resolving the id via the shared user-directory
 * cache. Falls back to the bare id while the lookup is pending or when
 * HR has no record (e.g. a departed user whose record was never merged).
 *
 * Use this in form/detail views that show a single userId. List views
 * that render N rows should batch-resolve through `useUserDisplayNames`
 * at the page level and call `formatUserDisplay` directly per row, so
 * the parent component owns the data dependency in one place.
 */
export function UserName({ id }: UserNameProps): ReactElement {
    const trimmed = typeof id === "string" ? id.trim() : "";
    const query = useUserDirectoryEntry(trimmed === "" ? null : trimmed);

    if (trimmed === "") {
        return <span>—</span>;
    }

    return <span>{formatUserDisplay(query.data ?? null, trimmed)}</span>;
}
